import {useEffect,useRef,useState,type PointerEvent,type MouseEvent as ReactMouseEvent,type ReactNode} from 'react';


/** Render only the small markup actually present in the owner-supplied
 * FloatHelm waiver text. Never insert HTML or interpret arbitrary scripts. */
function waiverInline(source:string):ReactNode[]{
  const nodes:ReactNode[]=[];
  const token=/\[([^\]]+)\]\((https:\/\/[^\s)]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  let cursor=0,match:RegExpExecArray|null;
  while((match=token.exec(source))!==null){
    if(match.index>cursor)nodes.push(source.slice(cursor,match.index));
    if(match[1]&&match[2])
      nodes.push(<a key={match.index} href={match[2]} target="_blank"
        rel="noopener noreferrer">{match[1]}</a>);
    else if(match[3])nodes.push(<strong key={match.index}>{match[3]}</strong>);
    else if(match[4])nodes.push(<em key={match.index}>{match[4]}</em>);
    cursor=match.index+match[0].length;
  }
  if(cursor<source.length)nodes.push(source.slice(cursor));
  return nodes;
}
function WaiverText({value}:{value:string}){
  return <span className="ecfs-waiver-formatted">
    {value.split('\n').map((line,i)=>{
      const trimmed=line.trim();
      const rule=trimmed==='---',bullet=trimmed.startsWith('- ');
      const content=bullet?trimmed.slice(2):line;
      return <span key={i} className={
        'ecfs-waiver-formatted-line'+(rule?' rule':'')+
        (bullet?' bullet':'')+(trimmed.startsWith('↳')?' indented':'')+
        (!trimmed?' blank':'')
      }>{rule?'':<>{bullet?'• ':''}{waiverInline(content)}</>}</span>;
    })}
  </span>;
}

type Point={x:number;y:number};
type Waiver={
  id:string;revision:number;title:string;initialText:string;sections:string[];
  sectionInstructions:string;afterText:string;readAndAgreeText:string;
  submitLabel:string;redirectUrl:string|null;requiresFullName:boolean;requiresSignature:boolean;
  acceptedAt:string|null;acceptedRevision:number|null;needsSignature:boolean;
};
type State='loading'|'ready'|'error';
type GuestWaiverRecord={ok:boolean;data?:{waivers:Waiver[]}};
const bounds=(v:number)=>Math.max(0,Math.min(1,v));
const xy=(e:PointerEvent<SVGSVGElement>):Point=>{
  const rect=e.currentTarget.getBoundingClientRect();
  return {x:bounds((e.clientX-rect.left)/rect.width),
    y:bounds((e.clientY-rect.top)/rect.height)};
};
function WaiverSigner({waiver,onSaved,locationCode}:{
  waiver:Waiver;onSaved:()=>void;locationCode:string;
}){
  const [name,setName]=useState('');
  const [checkedSections,setCheckedSections]=useState<number[]>([]);
  const [agreed,setAgreed]=useState(false);
  const [strokes,setStrokes]=useState<Point[][]>([]);
  const pointer=useRef<number|null>(null);
  const dragCleanup=useRef<null|(()=>void)>(null);
  useEffect(()=>()=>{dragCleanup.current?.();dragCleanup.current=null;},[]);
  const [requestKey]=useState(()=>crypto.randomUUID());
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState('');
  const drawStart=(e:PointerEvent<SVGSVGElement>)=>{
    if(pointer.current!==null)return;
    pointer.current=e.pointerId;
    // Initialize the drawn mark BEFORE requesting pointer capture. Some
    // embedded browsers reject capture on SVG and would otherwise discard
    // the down event entirely, leaving a permanently empty signature.
    // React may process a state updater after dispatch, when currentTarget is null.
    // Capture real pointer coordinates synchronously before scheduling the update.
    const startPoint=xy(e);
    setStrokes(old=>[...old,[startPoint]]);
    // React SVG pointermove can be dropped when capture changes ownership
    // during the rerender. Record the actual active pointer at the window
    // capture phase as well, including moves outside the SVG boundary.
    // This uses real pointer coordinates, not a generated signature.
    dragCleanup.current?.();
    const surface=e.currentTarget;
    const addPoint=(event:globalThis.PointerEvent)=>{
      if(pointer.current!==event.pointerId)return;
      const rect=surface.getBoundingClientRect();
      if(rect.width<=0||rect.height<=0)return;
      const point={x:bounds((event.clientX-rect.left)/rect.width),
        y:bounds((event.clientY-rect.top)/rect.height)};
      setStrokes(old=>old.map((stroke,i)=>
        i===old.length-1?[...stroke,point]:stroke));
    };
    const cleanup=()=>{
      window.removeEventListener('pointermove',addPoint,true);
      window.removeEventListener('pointerup',finish,true);
      window.removeEventListener('pointercancel',cancel,true);
    };
    const finish=(event:globalThis.PointerEvent)=>{
      if(pointer.current!==event.pointerId)return;
      addPoint(event);pointer.current=null;
      cleanup();dragCleanup.current=null;
    };
    const cancel=(event:globalThis.PointerEvent)=>{
      if(pointer.current!==event.pointerId)return;
      pointer.current=null;cleanup();dragCleanup.current=null;
    };
    window.addEventListener('pointermove',addPoint,true);
    window.addEventListener('pointerup',finish,true);
    window.addEventListener('pointercancel',cancel,true);
    dragCleanup.current=cleanup;
    // No SVG setPointerCapture: some Chromium and Android SVG backends
    // cancel the pointer immediately when React draws the first stroke.
    // The window-level handlers above retain the real drag instead.
  };
  const drawMove=(e:PointerEvent<SVGSVGElement>)=>{
    // Some Android WebViews and Chromium pointer capture transitions issue
    // pointercancel immediately after down. If the pen/mouse remains pressed,
    // resume a real stroke instead of silently discarding all movement.
    if(pointer.current!==e.pointerId){
      if(pointer.current!==null||e.buttons!==1)return;
      pointer.current=e.pointerId;
      // Continue the SAME physical stroke if an SVG pointercancel interrupted
      // capture. A series of single-point strokes is not a valid signature.
      setStrokes(old=>old.length?old.map((stroke,i)=>
        i===old.length-1?[...stroke,xy(e)]:stroke):[[xy(e)]]);
      return;
    }
    const point=xy(e);
    setStrokes(old=>old.map((stroke,i)=>
      i===old.length-1?[...stroke,point]:stroke));
  };
  const drawStop=(e:PointerEvent<SVGSVGElement>)=>{
    if(pointer.current!==e.pointerId)return;
    drawMove(e);pointer.current=null;
    try{
      if(e.currentTarget.hasPointerCapture(e.pointerId))
        e.currentTarget.releasePointerCapture(e.pointerId);
    }catch{ /* SVG capture may be unsupported by this browser. */ }
  };
  // Some browsers dispatch compatibility mouse-move events without a
  // matching React SVG pointermove when setPointerCapture is active.
  // Capture mouse movements as a fallback while a real pointer is held.
  const drawMouseDown=(e:ReactMouseEvent<SVGSVGElement>)=>{
    // Mouse fallback if pointerdown was not delivered (e.g. embedded webviews).
    // The pointer path wins when both events are dispatched.
    if(pointer.current!==null)return;
    pointer.current=-1;
    const rect=e.currentTarget.getBoundingClientRect();
    setStrokes(old=>[...old,[{x:bounds((e.clientX-rect.left)/rect.width),
      y:bounds((e.clientY-rect.top)/rect.height)}]]);
  };
  const drawMouseUp=(e:ReactMouseEvent<SVGSVGElement>)=>{
    if(pointer.current!==-1)return;
    const rect=e.currentTarget.getBoundingClientRect();
    const point={x:bounds((e.clientX-rect.left)/rect.width),
      y:bounds((e.clientY-rect.top)/rect.height)};
    setStrokes(old=>old.map((stroke,i)=>
      i===old.length-1?[...stroke,point]:stroke));
    pointer.current=null;
  };
  const drawMouseMove=(e:ReactMouseEvent<SVGSVGElement>)=>{
    if(e.buttons!==1)return;
    if(pointer.current===null){
      pointer.current=-1;
      const rect=e.currentTarget.getBoundingClientRect();
      const point={x:bounds((e.clientX-rect.left)/rect.width),
        y:bounds((e.clientY-rect.top)/rect.height)};
      setStrokes(old=>old.length?old.map((stroke,i)=>
        i===old.length-1?[...stroke,point]:stroke):[[point]]);
      return;
    }
    const rect=e.currentTarget.getBoundingClientRect();
    const point={x:bounds((e.clientX-rect.left)/rect.width),
      y:bounds((e.clientY-rect.top)/rect.height)};
    setStrokes(old=>old.map((stroke,i)=>
      i===old.length-1?[...stroke,point]:stroke));
  };

  const validSignature=strokes.some(stroke=>stroke.length>=3);
  const allChecks=checkedSections.length===waiver.sections.length;
  const ready=(!waiver.readAndAgreeText||agreed)&&allChecks&&(!waiver.requiresFullName||name.trim().length>=2)&&
    (!waiver.requiresSignature||validSignature)&&!busy;
  const sign=async()=>{
    if(!ready)return;
    setBusy(true);setError('');
    try{
      const response=await fetch('/api/v1/guest/waivers/sign',{
        method:'POST',credentials:'same-origin',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({
          location:locationCode,waiverId:waiver.id,revision:waiver.revision,
          requestKey,signedName:name.trim(),agreed:true,
          acknowledgedSections:[...checkedSections].sort((a,b)=>a-b),
          signatureStrokes:waiver.requiresSignature?strokes:[],
        }),
      });
      const body=await response.json() as {ok:boolean;error?:{code?:string}};
      if(!response.ok||!body.ok)
        throw Error(body.error?.code==='WAIVER_REVISION_CHANGED'
          ?'This waiver has changed. Reload to review the latest version.'
          :'The waiver could not be submitted. Please try again.');
      // Match the owner-provided Helm post-waiver redirect to the optional PWP.
      if(waiver.redirectUrl)window.location.assign(waiver.redirectUrl);
      else onSaved();
    }catch(e){setError(e instanceof Error?e.message:'Could not submit waiver.');}
    finally{setBusy(false);}
  };
  return <section className="ecfs-guest-waiver-form" aria-label={waiver.title+' waiver'}>
    <h3>{waiver.title}</h3>
    {waiver.initialText&&<div className="ecfs-waiver-copy"><WaiverText value={waiver.initialText}/></div>}
    {waiver.sectionInstructions&&<p><WaiverText value={waiver.sectionInstructions}/></p>}
    {waiver.sections.map((section,i)=><label key={i} className="ecfs-waiver-checkmark">
      <input type="checkbox" checked={checkedSections.includes(i)}
        onChange={e=>setCheckedSections(old=>e.target.checked
          ?[...old,i]:old.filter(n=>n!==i))}/>
      <span><WaiverText value={section}/></span>
    </label>)}
    {waiver.afterText&&<div className="ecfs-waiver-copy"><WaiverText value={waiver.afterText}/></div>}
    {waiver.requiresFullName&&<label className="field">
      Full name<input value={name} onChange={e=>setName(e.target.value)}
        autoComplete="name" maxLength={140} aria-label="Waiver full name"/>
    </label>}
    {waiver.requiresSignature&&<div className="ecfs-waiver-drawing">
      <strong>Signature</strong>
      <svg viewBox="0 0 600 150" role="img" aria-label="Draw your signature"
        onPointerDown={drawStart} onPointerMove={drawMove}
        onMouseDown={drawMouseDown} onMouseMove={drawMouseMove}
        onMouseUp={drawMouseUp}
        onPointerUp={drawStop} onPointerCancel={drawStop}
        style={{touchAction:'none',width:'100%',height:150,
          background:'#fff',border:'1px solid #a5b9cb',borderRadius:4,
          cursor:'crosshair',userSelect:'none'}}>
        {strokes.map((stroke,i)=><polyline key={i} fill="none"
          stroke="#143b59" strokeWidth={2.4} strokeLinecap="round"
          strokeLinejoin="round"
          points={stroke.map(p=>`${p.x*600},${p.y*150}`).join(' ')}/>)}
      </svg>
      <p role="status">{validSignature?'Signature captured.':'Draw your signature in the box above.'}</p>
      <button type="button" onClick={()=>{setStrokes([]);pointer.current=null;}}>
        Clear signature</button>
    </div>}
    {waiver.readAndAgreeText&&<label className="ecfs-waiver-checkmark">
      <input type="checkbox" checked={agreed}
        onChange={e=>setAgreed(e.target.checked)}/>
      <span>{waiver.readAndAgreeText}</span>
    </label>}
    {error&&<p role="alert">{error}</p>}
    <button type="button" disabled={!ready} onClick={()=>void sign()}>
      {busy?'Submitting…':waiver.submitLabel}</button>
  </section>;
}
export function GuestWaivers({locationCode}:{locationCode:string}){
  const [items,setItems]=useState<Waiver[]>([]);
  const [status,setStatus]=useState<State>('loading');
  const [reload,setReload]=useState(0);
  useEffect(()=>{
    const controller=new AbortController();
    setStatus('loading');
    void fetch('/api/v1/guest/waivers?'+new URLSearchParams({location:locationCode}),{
      credentials:'same-origin',cache:'no-store',signal:controller.signal,
    }).then(async r=>{
      if(!r.ok)throw Error('WAIVERS_UNAVAILABLE');
      const body=await r.json() as GuestWaiverRecord;
      if(!body.ok||!Array.isArray(body.data?.waivers))throw Error('WAIVERS_UNAVAILABLE');
      if(!controller.signal.aborted){setItems(body.data.waivers);setStatus('ready');}
    }).catch(()=>{if(!controller.signal.aborted)setStatus('error');});
    return ()=>controller.abort();
  },[locationCode,reload]);
  if(status==='loading'||status==='error'||items.length===0)return null;
  return <section className="ecfs-record-tab-content" aria-label="My waivers">
    <h2>Waivers</h2>
    {items.map(item=>item.needsSignature
      ?<WaiverSigner key={item.id+'-'+item.revision} waiver={item}
        locationCode={locationCode} onSaved={()=>setReload(n=>n+1)}/>
      :<p key={item.id}>{item.title}: Signed
        {item.acceptedAt?' on '+new Date(item.acceptedAt).toLocaleDateString():''}</p>)}
  </section>;
}
